import { apiFetch } from './client';

// Wire types for the UAT module. They mirror mi-portal-api/src/uat/types.ts and the shapes the
// routes in routes/uat.ts and routes/adminUat.ts return.

export type Outcome = 'pass' | 'fail' | 'blocked' | 'na';
export type Severity = 'P1' | 'P2' | 'P3' | 'P4';
export type StepStatus = 'not_run' | 'passed' | 'failed' | 'ready_to_retest' | 'changed';
export type PlanStatus = 'draft' | 'published' | 'closed';

/** REL-QA-001.4's scale, as testers see it. */
export const SEVERITY_LABEL: Record<Severity, string> = {
  P1: 'Go-live blocker',
  P2: 'Workaround exists, but unacceptable at go-live',
  P3: 'Cosmetic or annoyance',
  P4: 'Enhancement (goes to post-MVP, not break-fix)',
};

export interface Plan {
  id: string;
  name: string;
  status: PlanStatus;
  sourceFilename: string | null;
  createdAt: string;
  publishedAt: string | null;
  closedAt: string | null;
  signoffStatement?: string | null;
  signoffStatementVersion?: number;
}

export interface Section { id: string; code: string; title: string; sortOrder: number }

export interface Step {
  id: string;
  sectionId: string;
  testId: string;
  role: string | null;
  action: string;
  expected: string;
  revision: number;
  sortOrder: number;
  // Staff only.
  confirmNote?: string | null;
  confirmClearedNote?: string | null;
  confirmClearedBy?: string | null;
  confirmClearedAt?: string | null;
  retiredAt?: string | null;
  /** An instruction Mount Insights shows testers on this step. Not part of its text. */
  testerNote?: string | null;
  testerNoteAt?: string | null;
}

export interface StepRevision { stepId: string; revision: number; role: string | null; action: string; expected: string }
export interface Round { id: string; number: number; name: string; asanaSectionGid: string | null; openedAt: string; closedAt: string | null }

export interface Result {
  id: string;
  roundId: string;
  stepId: string;
  stepRevision: number;
  outcome: Outcome;
  notes: string | null;
  testerUserId: string;
  testerName: string | null;
  testerEmail: string | null;
  createdAt: string;
  /** Export only: a later reset of this step in this round cleared the result. */
  clearedByReset?: boolean;
}

/** An admin putting one step back to Not run in one round. Earlier results stay in the history. */
export interface StepReset {
  id: string;
  roundId: string;
  stepId: string;
  reason: string | null;
  resetBy: string | null;
  resetByName: string | null;
  createdAt: string;
}

export interface Defect {
  id: string;
  planId: string;
  roundId: string;
  stepId: string;
  resultId: string;
  severity: Severity;
  title: string;
  asanaTaskGid: string;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  asana?: { status: string | null; completed: boolean; visible: boolean } | null;
  /** Set by the page, never the API: raised by Mount Insights from a staff run. */
  mi?: boolean;
  /** Only on the response to logging: the report went onto the step's open task instead of a new one. */
  joined?: boolean;
}

/** The step's one open Asana task. Every tester's report and retest on the step lands on it. */
export interface StepTask { asanaTaskGid: string; title: string; status: string | null; visible: boolean }

/** Staff only: an open task whose reporters have all passed the step since. MI's to close. */
export interface RetestedIssue {
  asanaTaskGid: string;
  stepId: string;
  testId: string;
  title: string;
  severity: Severity;
  status: string | null;
  testerNames: string[];
  retestedAt: string;
}

export interface CoverageRow { key: string; planned: number; executed: number; passed: number; failed: number }

export interface PlanView {
  isStaff: boolean;
  plan: Plan;
  sections: Section[];
  steps: Step[];
  revisions: StepRevision[];
  rounds: Round[];
  round: Round | null;
  canTest: boolean;
  /** Staff admins and client admins, on the open round of a published plan. */
  canReset?: boolean;
  /** Staff admins and client admins, until the plan closes: may set the step instruction. */
  canNote?: boolean;
  /** Resets in the round being viewed. */
  resets?: StepReset[];
  /** Every round's results (cleared ones marked) and resets: the dated trail on each step. */
  history?: { results: Result[]; resets: StepReset[] };
  /** The open task for each step that has one, by step id. */
  stepTasks?: Record<string, StepTask>;
  /** Staff only. */
  retestedOpen?: RetestedIssue[];
  /** The caller's own id: a client user's, or for staff the id their staff runs carry. */
  me: string | null;
  /** Client testers' results only. */
  results: Result[];
  /** Issues raised by client testers only. */
  defects: Defect[];
  /** Staff only: Mount Insights runs in this round. They never count toward status or coverage. */
  staffResults?: Result[];
  /** Staff only: issues raised from staff runs, every round. */
  staffDefects?: Defect[];
  stale: boolean;
  status: Record<string, StepStatus>;
  /** False for a step that passed earlier and this round does not retest. */
  inScope: Record<string, boolean>;
  /** The round a carried step's status comes from. */
  carriedFrom: Record<string, number | null>;
  /** A result counts toward this round: recorded in it, or carried. A step awaiting retest is false. */
  tested?: Record<string, boolean>;
  coverage: { byRole: CoverageRow[]; bySection: CoverageRow[]; total: CoverageRow };
}

export interface ExportRound extends Round {
  status: Record<string, StepStatus>;
  inScope: Record<string, boolean>;
  coverage: { byRole: CoverageRow[]; bySection: CoverageRow[]; total: CoverageRow };
}

export interface PlanExport {
  exportedAt: string;
  isStaff: boolean;
  plan: Plan;
  sections: Section[];
  steps: Step[];
  rounds: ExportRound[];
  results: Result[];
  resets?: StepReset[];
  defects: Defect[];
  /** Staff exports only. */
  staffResults?: Result[];
  staffDefects?: Defect[];
  stale: boolean;
}

/** One comment on a step's Asana task, as the viewer may read it. */
export interface ThreadComment {
  gid: string | null;
  author: string;
  text: string;
  createdAt: string;
  /** Typed in Asana, or posted from the portal by Mount Insights staff. */
  fromMi: boolean;
  /** Staff only: MI has hidden this comment from the client. */
  hidden?: boolean;
}

export interface StepThread {
  task: { asanaTaskGid: string; title: string; status: string | null; visible: boolean; closed: boolean } | null;
  comments: ThreadComment[];
  canPost: boolean;
  /** True when there is no open task: a comment now starts one. */
  startsNew: boolean;
  /** Why posting is off, when it is. */
  reason: 'board_not_configured' | 'comments_disabled' | 'plan_not_open' | 'creation_disabled' | 'with_mount_insights' | 'view_only' | null;
  stale: boolean;
}

const base = (slug: string) => `/clients/${encodeURIComponent(slug)}/modules/uat`;

export const listPlans = (slug: string, token: string | null) => apiFetch<{ isStaff: boolean; plans: Plan[] }>(`${base(slug)}/`, token);
export const getPlan = (slug: string, planId: string, roundId: string | null, token: string | null) =>
  apiFetch<PlanView>(`${base(slug)}/plans/${planId}${roundId ? `?round=${encodeURIComponent(roundId)}` : ''}`, token);
export interface AcceptedSnapshot {
  round: { id: string; number: number; name: string } | null;
  coverage: { planned: number; executed: number; passed: number; failed: number };
  openIssues: { severity: Severity; title: string; testId: string; status: string | null; visible: boolean; asanaTaskGid: string; loggedAt: string; raisedBy?: 'mi' }[];
  resolvedIssues: number;
}

export interface UatSignoff {
  id: string;
  planId: string;
  roundId: string | null;
  kind: 'signed' | 'revoked';
  revokesId: string | null;
  statementVersion: number | null;
  statementText: string | null;
  accepted: AcceptedSnapshot | null;
  signerName: string | null;
  signerEmail: string;
  signerRole: string;
  createdAt: string;
}

export interface SignoffView {
  statement: { text: string; version: number } | null;
  signer: { roleLabel: string } | null;
  isStaff: boolean;
  signoffs: UatSignoff[];
  standing: string[];
  /** The caller's own standing sign-off, if any. */
  mine?: string | null;
  preview: AcceptedSnapshot;
  previewComplete: boolean;
}

export const getSignoff = (slug: string, planId: string, token: string | null) => apiFetch<SignoffView>(`${base(slug)}/plans/${planId}/signoff`, token);
export const signOffPlan = (slug: string, planId: string, token: string | null) =>
  apiFetch<UatSignoff>(`${base(slug)}/plans/${planId}/signoff`, token, { method: 'POST', body: '{}', timeoutMs: 45_000 });
export const revokeUatSignoff = (slug: string, signoffId: string, token: string | null) =>
  apiFetch<UatSignoff>(`${base(slug)}/signoffs/${signoffId}/revoke`, token, { method: 'POST' });
export const exportPlan = (slug: string, planId: string, token: string | null) =>
  apiFetch<PlanExport>(`${base(slug)}/plans/${planId}/export`, token, { timeoutMs: 60_000 });
export const recordResult = (slug: string, planId: string, body: { roundId: string; stepId: string; outcome: Outcome; notes: string | null }, token: string | null) =>
  apiFetch<Result>(`${base(slug)}/plans/${planId}/results`, token, { method: 'POST', body: JSON.stringify(body) });
/** `title` can be empty when the step already has an open task: the report is added to it. */
export const getStepThread = (slug: string, planId: string, stepId: string, token: string | null) =>
  apiFetch<StepThread>(`${base(slug)}/plans/${planId}/steps/${stepId}/thread`, token, { timeoutMs: 30_000 });
export const postStepComment = (slug: string, planId: string, stepId: string, text: string, token: string | null) =>
  apiFetch<{ created: boolean; task: StepThread['task']; comment: ThreadComment }>(`${base(slug)}/plans/${planId}/steps/${stepId}/comments`, token, { method: 'POST', body: JSON.stringify({ text }), timeoutMs: 45_000 });
export const logDefect = (slug: string, planId: string, body: { resultId: string; title: string; whatHappened: string; severity: Severity }, token: string | null) =>
  apiFetch<Defect>(`${base(slug)}/plans/${planId}/defects`, token, { method: 'POST', body: JSON.stringify(body), timeoutMs: 45_000 });
export const attachScreenshot = (slug: string, defectId: string, body: { name: string; contentType: string; dataBase64: string }, token: string | null) =>
  apiFetch<{ name: string }>(`${base(slug)}/defects/${defectId}/attachments`, token, { method: 'POST', body: JSON.stringify(body), timeoutMs: 90_000 });

// ---------- admin ----------

export interface ImportRow {
  sectionCode: string;
  sectionTitle: string;
  testId: string;
  role: string | null;
  action: string;
  expected: string;
  miComment: string | null;
}

export interface ImportSummary {
  dryRun: boolean;
  planId?: string;
  sections: number;
  steps: number;
  added: number;
  updated: number;
  missing: string[];
  padded: { from: string; to: string }[];
  confirmCount: number;
  errors: string[];
}

export interface Readiness { ready: boolean; checks: { key: string; ok: boolean; label: string }[] }
export interface PlanDetail { plan: Plan; sections: Section[]; steps: Step[]; revisions: StepRevision[]; rounds: Round[]; roundScopes?: { roundId: string; stepId: string }[] }

const admin = (clientId: string) => `/admin/clients/${encodeURIComponent(clientId)}/uat`;

export const getAdminState = (clientId: string, token: string | null) => apiFetch<{ plans: Plan[]; readiness: Readiness }>(`${admin(clientId)}/`, token);
export const getAdminPlan = (clientId: string, planId: string, token: string | null) => apiFetch<PlanDetail>(`${admin(clientId)}/plans/${planId}`, token);
export const getBoardSections = (clientId: string, token: string | null) =>
  apiFetch<{ sections: { gid: string; name: string }[]; intakeSectionGid: string | null }>(`${admin(clientId)}/sections`, token);
export const importPlan = (clientId: string, body: { planId: string | null; name: string | null; source: string; dryRun: boolean; rows: ImportRow[]; sheetErrors?: string[] }, token: string | null) =>
  apiFetch<ImportSummary>(`${admin(clientId)}/import`, token, { method: 'POST', body: JSON.stringify(body), timeoutMs: 60_000 });
export const editStep = (clientId: string, stepId: string, patch: { role?: string | null; action?: string; expected?: string; sortOrder?: number }, token: string | null) =>
  apiFetch<Step>(`${admin(clientId)}/steps/${stepId}`, token, { method: 'PATCH', body: JSON.stringify(patch) });
export const clearConfirm = (clientId: string, stepId: string, note: string, token: string | null) =>
  apiFetch<{ ok: true }>(`${admin(clientId)}/steps/${stepId}/confirm`, token, { method: 'POST', body: JSON.stringify({ note }) });
export const setStepNote = (clientId: string, stepId: string, note: string | null, token: string | null) =>
  apiFetch<Step>(`${admin(clientId)}/steps/${stepId}/note`, token, { method: 'PUT', body: JSON.stringify({ note }) });
export const resetStep = (clientId: string, stepId: string, reason: string | null, token: string | null) =>
  apiFetch<StepReset>(`${admin(clientId)}/steps/${stepId}/reset`, token, { method: 'POST', body: JSON.stringify({ reason }) });
export const retireStep = (clientId: string, stepId: string, retired: boolean, token: string | null) =>
  apiFetch<{ ok: true }>(`${admin(clientId)}/steps/${stepId}/retire`, token, { method: 'POST', body: JSON.stringify({ retired }) });
/** What replacing one plan with another would add, drop and change, by Test ID. */
export interface PlanDiff {
  added: string[];
  removed: string[];
  changed: string[];
  unchanged: number;
}

/**
 * With replacesPlanId, the server closes that published plan in the same step: a new version
 * replacing an old one. It refuses while steps would be dropped unless acknowledgeRemoved is set.
 */
export const publishPlan = (clientId: string, planId: string, token: string | null, replacesPlanId?: string | null, acknowledgeRemoved = false) =>
  apiFetch<{ ok: true; round: Round; replaced?: { id: string; name: string } }>(`${admin(clientId)}/plans/${planId}/publish`, token,
    { method: 'POST', ...(replacesPlanId ? { body: JSON.stringify({ replacesPlanId, acknowledgeRemoved }) } : {}) });
/** The comparison shown before replacing a plan. Writes nothing. */
export const previewReplace = (clientId: string, planId: string, replacesPlanId: string, token: string | null) =>
  apiFetch<{ dryRun: true; replaces: { id: string; name: string } } & PlanDiff>(`${admin(clientId)}/plans/${planId}/publish`, token,
    { method: 'POST', body: JSON.stringify({ replacesPlanId, dryRun: true }) });
export const closePlan = (clientId: string, planId: string, token: string | null) =>
  apiFetch<{ ok: true }>(`${admin(clientId)}/plans/${planId}/close`, token, { method: 'POST' });

/** What clearing a plan's results, or deleting the plan, removes. The Asana tasks are left on the board. */
export interface PurgeCounts {
  results: number; staffResults: number; defects: number; staffDefects: number; stepTasks: number; resets: number;
  asanaTasks: number; rounds: number; steps: number; sections: number;
}
export interface PurgePreview {
  dryRun: true;
  plan: { id: string; name: string; status: Plan['status'] };
  counts: PurgeCounts;
  /** Sign-offs on the plan. Any at all, and nothing on it can be deleted. */
  signoffs: number;
}
export type PurgeKind = 'results' | 'plan';
const purgePath = (kind: PurgeKind) => (kind === 'plan' ? 'delete' : 'purge-results');

/** Restarting a round puts every step with a result back to Not run. Nothing is deleted. */
export const previewRestart = (clientId: string, planId: string, token: string | null) =>
  apiFetch<{ dryRun: true; round: { number: number; name: string }; steps: number }>(`${admin(clientId)}/plans/${planId}/restart-round`, token,
    { method: 'POST', body: JSON.stringify({ dryRun: true }) });
export const restartRound = (clientId: string, planId: string, reason: string | null, token: string | null) =>
  apiFetch<{ dryRun: false; reset: number }>(`${admin(clientId)}/plans/${planId}/restart-round`, token,
    { method: 'POST', body: JSON.stringify({ dryRun: false, reason }) });
/** Administrators only. A preview writes nothing; the real call needs the plan's name typed back. */
export const previewPurge = (clientId: string, planId: string, kind: PurgeKind, token: string | null) =>
  apiFetch<PurgePreview>(`${admin(clientId)}/plans/${planId}/${purgePath(kind)}`, token, { method: 'POST', body: JSON.stringify({ dryRun: true }) });
export const purgePlan = (clientId: string, planId: string, kind: PurgeKind, confirm: string, token: string | null) =>
  apiFetch<{ dryRun: false; counts: PurgeCounts }>(`${admin(clientId)}/plans/${planId}/${purgePath(kind)}`, token,
    { method: 'POST', body: JSON.stringify({ dryRun: false, confirm }) });
export const openNextRound = (clientId: string, planId: string, body: { name?: string | null; asanaSectionGid?: string | null; dryRun: boolean }, token: string | null) =>
  apiFetch<{ dryRun: boolean; number?: number; retest: number; carried: number; round?: Round }>(`${admin(clientId)}/plans/${planId}/rounds`, token, { method: 'POST', body: JSON.stringify(body) });
export const setStatement = (clientId: string, planId: string, text: string | null, token: string | null) =>
  apiFetch<Plan>(`${admin(clientId)}/plans/${planId}/statement`, token, { method: 'PUT', body: JSON.stringify({ text }) });
export const getUatSigners = (clientId: string, token: string | null) =>
  apiFetch<{ signers: { userId: string; email: string; name: string | null; roleLabel: string }[]; clientUsers: { userId: string; email: string; name: string | null }[] }>(`${admin(clientId)}/signers`, token);
export const setUatSigner = (clientId: string, userId: string, roleLabel: string | null, token: string | null) =>
  apiFetch<{ ok: true }>(`${admin(clientId)}/signers/${userId}`, token, { method: 'PUT', body: JSON.stringify({ roleLabel }) });
export const updateRound = (clientId: string, roundId: string, body: { name: string; asanaSectionGid: string | null }, token: string | null) =>
  apiFetch<{ ok: true }>(`${admin(clientId)}/rounds/${roundId}`, token, { method: 'PUT', body: JSON.stringify(body) });

export { errorCode, errorMessage } from './uiSignoff';
